import React from 'react';
import styled from 'styled-components';
import * as remoteApi from 'api/remote';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import { DragHandle, WidgetContainer, WidgetContent, WidgetHeader } from '.';
import { DashboardWidget, Workspace } from 'types';
import KoalaIcon from 'koala/components/Icons';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { shallowEqual, useSelector } from 'react-redux';
import KoalaLoader from 'koala/components/Loader';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import KoalaCircularProgress from 'koala/components/CircularProgress';

const Container = styled.div`
  height: 18rem;
  display: flex;
  width: 100%;
  height: 90%;
  max-height: 100%;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.x4};
  height: 100%;
  flex-direction: column;

  @media ${theme.devices.mobile} {
    max-height: 25rem;
  }
`;

interface Props {
  widget: DashboardWidget;
}

interface FilterParams {
  archived: boolean;
  is_in_progress: boolean;
  plan_id?: string[];
  id?: string[];
  team_id?: string[];
}

function InitiativeProgressWidget(props: Props) {
  const { widget } = props;
  const currentWorkspace: Workspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t } = useTranslation();

  const widgetTitle: string = widget.title
    ? widget.title
    : t('workspaceDashboards.initiative-progress', {
        initiative: translate(currentWorkspace, CustomTermKey.INITIATIVE, 1),
      });

  const initiativesFilterParams: FilterParams = {
    archived: false,
    is_in_progress: true,
  };

  const queryKeyInitiativesStats = [
    queryKeys.initiatives,
    'stats',
    {
      workspaceSlug: currentWorkspace.slug,
      filter: initiativesFilterParams,
    },
  ];
  const staleTime = queryKeys.staleTime;
  const {
    data: initiativesStatsData,
    isLoading,
    isFetching,
  } = useQuery(queryKeyInitiativesStats, remoteApi.fetchInitiativesStats, {
    staleTime,
  });

  let initiativeProgressPrct = 0;
  const initiativesStats = initiativesStatsData?.data;

  if (!initiativesStats) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <DragHandle className="drag-handle">
            <KoalaIcon iconName="grab" iconSize="small" />
          </DragHandle>
          <p className="widget-type">{widgetTitle}</p>
        </WidgetHeader>
        <WidgetContent>
          <p>{t('workspaceDashboards.widgetError')}</p>
        </WidgetContent>
      </WidgetContainer>
    );
  }
  let closedInitiativesCount = initiativesStats.done_initiatives_count + initiativesStats.abandoned_initiatives_count;
  if (initiativesStats.total_initiatives_count > 0) {
    initiativeProgressPrct = closedInitiativesCount / initiativesStats.total_initiatives_count;
  }

  const initiativeProgressData = {
    progressPercentage: initiativeProgressPrct * 100,
    colorType: 'B',
    centerLabel: [`${closedInitiativesCount}`, `/${initiativesStats.total_initiatives_count}`],
  };

  return (
    <WidgetContainer>
      <WidgetHeader>
        <DragHandle className="drag-handle">
          <KoalaIcon iconName="grab" iconSize="small" />
        </DragHandle>
        <p className="widget-type">{widgetTitle}</p>
      </WidgetHeader>
      <WidgetContent>
        {(isLoading || isFetching) && <KoalaLoader />}
        <Container className="panel-checkins-chart">
          <KoalaCircularProgress data={initiativeProgressData} size="large" />
        </Container>
      </WidgetContent>
    </WidgetContainer>
  );
}

export default InitiativeProgressWidget;
